import { gql, QueryHookOptions, useQuery } from '@apollo/client';
import { Question } from '@jupiterone/jupiter-types';
import { PageInfo } from '../types';

export const ListQuestionsQuery = gql`
  query ListQuestions(
    $searchQuery: String
    $integrationDefinitionId: String
    $tags: [String]
    $type: ListQuestionsType
    $limit: Int
    $cursor: String
  ) {
    questions(
      searchQuery: $searchQuery
      integrationDefinitionId: $integrationDefinitionId
      tags: $tags
      type: $type
      limit: $limit
      cursor: $cursor
    ) {
      questions {
        id
        sourceId
        title
        description
        tags
        queries {
          name
          query
          version
          resultsAre
        }
        compliance {
          standard
          requirements
          controls
        }
        variables {
          name
          required
          default
        }
        accountId
        integrationDefinitionId
        showTrend
      }
      totalHits
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export enum ListQuestionsType {
  ACCOUNT_AND_MANAGED = 'ACCOUNT_AND_MANAGED',
  ACCOUNT_ONLY = 'ACCOUNT_ONLY',
  MANAGED_ONLY = 'MANAGED_ONLY',
}

export type ListQuestionsResponse = {
  questions: {
    questions: Question[];
    pageInfo: PageInfo;
  };
};

export type ListQuestionsVariables = {
  searchQuery?: string;
  integrationDefinitionId?: string;
  tags?: string[];
  limit?: number;
  cursor?: string;
  type?: ListQuestionsType;
};

type Options = QueryHookOptions<ListQuestionsResponse, ListQuestionsVariables>;

export const useListQuestions = (options?: Options) => {
  const { data, ...response } = useQuery<
    ListQuestionsResponse,
    ListQuestionsVariables
  >(ListQuestionsQuery, options);

  const loadMore = async () => {
    const cursor = data?.questions.pageInfo.endCursor;
    await response.fetchMore({
      query: ListQuestionsQuery,
      variables: {
        ...options?.variables,
        cursor,
      },
      updateQuery: (
        previousResult,
        { fetchMoreResult }: { fetchMoreResult?: ListQuestionsResponse }
      ) => {
        const previousQuestions = previousResult.questions.questions;
        const newQuestions = fetchMoreResult?.questions.questions || [];
        const newQueryResult: ListQuestionsResponse = {
          ...fetchMoreResult,
          questions: {
            ...previousResult.questions,
            ...fetchMoreResult?.questions,
            questions: [...previousQuestions, ...newQuestions],
          },
        };
        return newQueryResult;
      },
    });
  };

  return {
    questions: data?.questions.questions,
    pageInfo: data?.questions.pageInfo,
    loadMore,
    ...response,
  };
};
